import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { errorFromResponse } from "@/services/apiError";
import { currentViewerTimeZone } from "@/utils/viewerTimeZone";
import { QueryClient } from "@tanstack/react-query";
import { ENTITY_QUERY_KEYS } from "../Data/entity";
import { SCHEMA_QUERY_KEYS } from "../Data/schema";
import { RMS_BOOTSTRAP_QUERY_KEY } from "../Data/bootstrap";
import { isApproveAffectedQuery } from "./approveAffectedQueryKeys";
import type { DetachOutcome } from "./detachResult";
import type {
  AddressComponent,
  AddressValue,
  AttributeDesignation,
  DocumentConfig,
  DocumentGroup,
  DocumentType,
  EntityKind,
  EntitySchema,
  EntityUpdatePayload,
  EntityCreationPayload,
  EntityCreationFact,
  EntityFieldValue,
  DeletionResult,
  KindChangePlan,
  KindChangeResult,
  RmsSchema,
  SchemaChanges,
  SchemaFact,
  SchemaOp,
  AttributeDetail,
  ShallowEntity,
  ShallowSearchResult,
  ShallowSearchResponse,
  InteractionResponse,
  InteractionByBlobResponse,
  InteractionCreatePayload,
  InteractionUpdatePayload,
  EntityDocumentGroupsResponse,
  EntityDocumentsPage,
  EntityVehicleDocumentsPage,
  EntityInteractionsPage,
} from "./types";

/** What the quick record search can be asked. */
export interface SearchEntitiesOptions {
  searchTerm?: string;
  entityTypes?: EntityKind[];
  attributes?: string[];
  entityIds?: string[];
  allowCreatePerson?: boolean;
  includeInteractions?: boolean;
  // A blank search term returns the organization's newest records instead of
  // nothing (#12154: the chat "@" menu opens before anything is typed).
  recent?: boolean;
}

export const searchEntities = (
  searchTerm: string = "",
  entityTypes?: EntityKind[],
  attributes?: string[],
  entityIds?: string[],
  allowCreatePerson?: boolean,
  includeInteractions?: boolean
): Promise<ShallowSearchResponse> =>
  searchEntitiesWith({
    searchTerm,
    entityTypes,
    attributes,
    entityIds,
    allowCreatePerson,
    includeInteractions,
  });

/** `searchEntities` by name, for callers past its first argument or two. */
export const searchEntitiesWith = async ({
  searchTerm = "",
  entityTypes,
  attributes,
  entityIds,
  allowCreatePerson,
  includeInteractions,
  recent,
}: SearchEntitiesOptions): Promise<ShallowSearchResponse> => {
  const params = new URLSearchParams({ q: searchTerm });

  if (entityTypes && entityTypes.length > 0) {
    entityTypes.forEach((kind) => params.append("kind", kind));
  }

  if (attributes && attributes.length > 0) {
    attributes.forEach((attr) => params.append("attributes", attr));
  }

  if (entityIds && entityIds.length > 0) {
    entityIds.forEach((id) => params.append("entity_ids", id));
  }

  if (allowCreatePerson) {
    params.append("allow_create_person", "true");
  }

  if (includeInteractions) {
    params.append("include_interactions", "true");
  }

  if (recent) {
    params.append("recent", "true");
  }

  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/search/shallow?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to search entities: ${response.status}`
    );
  }

  const data = await response.json();
  return data as ShallowSearchResponse;
};

export interface PrefetchBatchRequest {
  entities: { kind: EntityKind | string; id: string }[];
  interactions: string[];
}

export interface PrefetchBatchResponse {
  entities: EntitySchema[];
  interactions: InteractionResponse[];
}

export const fetchPrefetchBatch = async (
  payload: PrefetchBatchRequest
): Promise<PrefetchBatchResponse> => {
  // The batch fills the record cache, so its records' derived instants are
  // built in the viewer's zone, as a fresh read's are (#14680).
  const response = await fpFetch(`${BASE_API_URL_V2}/api/v2/rms/prefetch`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...payload, user_tz: currentViewerTimeZone() }),
  });

  if (!response.ok) {
    throw new Error(`Failed to prefetch batch: ${response.status}`);
  }

  return (await response.json()) as PrefetchBatchResponse;
};

export const fetchEntity = async (
  entityKind: EntityKind | string,
  entityId: string
): Promise<EntitySchema> => {
  // The derived instant facts (Date Created, Last Interaction, ...) render in
  // the viewer's zone, as the search bodies ask for theirs (#14680).
  const params = new URLSearchParams({ user_tz: currentViewerTimeZone() });
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to fetch entity: ${response.status}`
    );
  }

  const data = await response.json();

  return data;
};

// Lazy-loaded entity sub-resources (issue #9657). The entity GET payload
// no longer carries documents or interactions — these endpoints serve
// them on demand so the dashboard load is bounded for large entities.

export const fetchEntityDocumentGroups = async (
  entityKind: EntityKind | string,
  entityId: string,
  opts: {
    search?: string;
    // The org's configured grouping attributes (#11141). Omitted when the org
    // configured none, which is what keeps the response on its old shape.
    groupBy?: string;
    subgroupBy?: string;
  } = {}
): Promise<EntityDocumentGroupsResponse> => {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.groupBy) params.set("group_by", opts.groupBy);
  if (opts.subgroupBy) params.set("subgroup_by", opts.subgroupBy);
  const qs = params.toString();
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}/document-groups${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch document groups: ${response.status}`);
  }
  return (await response.json()) as EntityDocumentGroupsResponse;
};

export const fetchEntityDocuments = async (
  entityKind: EntityKind | string,
  entityId: string,
  opts: {
    group?: string;
    // Which attribute `group` / `subgroup` name a bucket of (#11141). Absent
    // means the document group, the only thing this endpoint used to group by.
    groupBy?: string;
    subgroupBy?: string;
    subgroup?: string;
    vehicleId?: string;
    // The record's own documents in ANY section, each once (#11962) — the
    // presence question. The default answers with the section no related
    // vehicle claims, which for a manager sharing every document with its
    // funds is empty.
    anySection?: boolean;
    cursor?: string | null;
    limit?: number;
    search?: string;
    sort?: "uploaded_at" | "document_date";
    order?: "asc" | "desc";
    documentTypes?: string[];
  } = {}
): Promise<EntityDocumentsPage | EntityVehicleDocumentsPage> => {
  const params = new URLSearchParams();
  // `!= null` rather than truthiness: the empty string is a real bucket (the
  // documents with no value for the attribute), and dropping it would page
  // the whole group instead.
  if (opts.group != null) params.set("group", opts.group);
  if (opts.groupBy) params.set("group_by", opts.groupBy);
  if (opts.subgroupBy) params.set("subgroup_by", opts.subgroupBy);
  if (opts.subgroup != null) params.set("subgroup", opts.subgroup);
  if (opts.vehicleId) params.set("vehicle_id", opts.vehicleId);
  if (opts.anySection) params.set("any_section", "true");
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.search) params.set("search", opts.search);
  if (opts.sort) params.set("sort", opts.sort);
  if (opts.order) params.set("order", opts.order);
  if (opts.documentTypes && opts.documentTypes.length > 0)
    params.set("document_type", opts.documentTypes.join(","));
  const qs = params.toString();
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}/documents${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch documents: ${response.status}`);
  }
  return await response.json();
};

// Distinct document types present in a folder, powering the per-folder
// Document Type filter dropdown on the Documents tab (#10062).
export const fetchEntityDocumentTypes = async (
  entityKind: EntityKind | string,
  entityId: string,
  opts: {
    group?: string;
    groupBy?: string;
    subgroupBy?: string;
    subgroup?: string;
    vehicleId?: string;
    search?: string;
  } = {}
): Promise<string[]> => {
  const params = new URLSearchParams();
  // Same empty-string-is-a-bucket rule as fetchEntityDocuments.
  if (opts.group != null) params.set("group", opts.group);
  if (opts.groupBy) params.set("group_by", opts.groupBy);
  if (opts.subgroupBy) params.set("subgroup_by", opts.subgroupBy);
  if (opts.subgroup != null) params.set("subgroup", opts.subgroup);
  if (opts.vehicleId) params.set("vehicle_id", opts.vehicleId);
  if (opts.search) params.set("search", opts.search);
  const qs = params.toString();
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}/document-types${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch document types: ${response.status}`);
  }
  const body = (await response.json()) as { types: string[] };
  return body.types;
};

export const fetchEntityInteractions = async (
  entityKind: EntityKind | string,
  entityId: string,
  opts: {
    cursor?: string | null;
    limit?: number;
    search?: string;
    /** Only meetings the record took part in, filtered before the limit
     *  (#12854). Unset, mentioned meetings come back too. */
    participantOnly?: boolean;
    /** Only upcoming meetings (true), or everything else (false). */
    upcoming?: boolean;
    /** Only these interaction types, by id, filtered before the limit (#14547).
     *  Unset or empty, every type comes back. */
    typeIds?: readonly number[];
  } = {}
): Promise<EntityInteractionsPage> => {
  const params = new URLSearchParams();
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.search) params.set("search", opts.search);
  if (opts.participantOnly) params.set("participant_only", "true");
  if (opts.upcoming != null) params.set("upcoming", String(opts.upcoming));
  for (const typeId of opts.typeIds ?? []) {
    params.append("type_id", String(typeId));
  }
  const qs = params.toString();
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}/interactions${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch interactions: ${response.status}`);
  }
  return (await response.json()) as EntityInteractionsPage;
};

// Overloaded function signatures for updateEntity
export async function updateEntity(
  queryClient: QueryClient,
  entityKind: EntityKind | string,
  entityId: string,
  name: string,
  facts?: Record<
    string,
    { value: EntityFieldValue } | { values: EntityFieldValue[] } | null
  >
): Promise<EntitySchema>;

export async function updateEntity(
  queryClient: QueryClient,
  entityKind: EntityKind | string,
  entityId: string,
  payload: EntityUpdatePayload
): Promise<EntitySchema>;

export async function updateEntity(
  queryClient: QueryClient,
  entityKind: EntityKind | string,
  entityId: string,
  nameOrPayload: string | EntityUpdatePayload,
  facts?: Record<
    string,
    { value: EntityFieldValue } | { values: EntityFieldValue[] } | null
  >
): Promise<EntitySchema> {
  let payload: EntityUpdatePayload;

  if (typeof nameOrPayload === "string") {
    // Legacy signature: updateEntity(queryClient, entityKind, entityId, name, facts)
    payload = {
      name: nameOrPayload,
      facts: facts || {},
    };
  } else {
    // New signature: updateEntity(queryClient, entityKind, entityId, payload)
    payload = nameOrPayload;
  }

  // The answer is written into the record cache below, so its derived
  // instants are built in the viewer's zone, as a fresh read's are (#14680).
  const zone = new URLSearchParams({ user_tz: currentViewerTimeZone() });
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}?${zone.toString()}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // If it's a structured error (ErrorEntitiesViolateRule or ErrorSchemaInvalid)
    if (errorData && typeof errorData === "object" && "message" in errorData) {
      throw errorData; // Throw the entire error object
    }

    // Otherwise throw a regular Error
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to update entity: ${response.status}`
    );
  }

  const updatedEntity = await response.json();

  // Update the React Query cache with the fresh entity data
  queryClient.setQueryData(
    ENTITY_QUERY_KEYS.entity(entityKind as EntityKind, entityId),
    updatedEntity
  );

  // Invalidate entire RMS cache to avoid any stale relationship bugs. Card
  // hydration lives under "rms" too, so a status update shows on the cards.
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "rms",
  });

  return updatedEntity;
}

export type ReviewApproval =
  | { kind: "entity"; entity_id: string }
  // cascade defaults to true (the interaction and everything staged under
  // it); false approves the interaction record alone.
  | { kind: "interaction"; interaction_id: string; cascade?: boolean }
  // Document approval (P1 backend support): a blob is staged through its
  // entity<->document link rows, addressed here by blob id. Sent by the
  // Activity view's review mode "send for review" action for document rows.
  | { kind: "document"; document_id: string }
  // One link on one interaction (#12298): the participant/mention row joining
  // it to an entity, or the attachment row joining it to a document. Nothing
  // about the entity or blob elsewhere.
  | { kind: "interaction_link"; interaction_id: string; entity_id: string }
  | {
      kind: "interaction_document";
      interaction_id: string;
      document_id: string;
    }
  // One field on one interaction: "summary" or "custom_field:<column id>".
  | { kind: "interaction_field"; interaction_id: string; field_name: string };

export const approveReview = async (
  queryClient: QueryClient,
  approvals: ReviewApproval[]
): Promise<{ approved_count: number }> => {
  const url = new URL(`${BASE_API_URL_V2}/api/v2/rms/review/approve`);
  const res = await fpFetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ approvals }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to approve");
  }
  const body = await res.json();
  await queryClient.invalidateQueries({
    predicate: isApproveAffectedQuery,
    refetchType: "active",
  });
  return body;
};

// A skip marks a pushable object (document / interaction) reviewed but holds it
// back from the Bipsync push (#10267) -- "Reviewed, skipped". Terminal until the
// object is re-edited.
export type ReviewSkip =
  | { kind: "interaction"; interaction_id: string }
  | { kind: "document"; document_id: string };

export const skipReview = async (
  queryClient: QueryClient,
  skips: ReviewSkip[]
): Promise<{ skipped_count: number }> => {
  const url = new URL(`${BASE_API_URL_V2}/api/v2/rms/review/skip`);
  const res = await fpFetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ skips }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to skip");
  }
  const body = await res.json();
  // The skipped rows flip status, so refetch the same surfaces approve touches.
  await queryClient.invalidateQueries({
    predicate: isApproveAffectedQuery,
    refetchType: "active",
  });
  return body;
};

export const deleteEntity = async (
  queryClient: QueryClient,
  entityKind: EntityKind | string,
  entityId: string,
  targetEntityId?: string
): Promise<DeletionResult> => {
  const url = new URL(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}`
  );
  if (targetEntityId) {
    url.searchParams.set("target_entity_id", targetEntityId);
  }

  const response = await fpFetch(url.toString(), {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Check if it's a referenced entity error
    if (errorData.referenced_by) {
      throw {
        message: errorData.message || "Entity is referenced by other entities",
        referenced_by: errorData.referenced_by,
      };
    }

    throw new Error(
      errorData.message || `Failed to delete entity: ${response.status}`
    );
  }

  const result = await response.json();

  // Drop search query caches so the next observer attach refetches without
  // the deleted entity. Visible-table clearing is the caller's
  // responsibility via `useRefreshRmsSearch` — it has React context and
  // can iterate every per-view store. (#9598)
  queryClient.removeQueries({
    predicate: (query) =>
      query.queryKey[0] === "rms" && query.queryKey[1] === "search",
  });

  // Invalidate other RMS caches (entity details, schema, etc.) to trigger refetch
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "rms" && query.queryKey[1] !== "search",
  });

  return result as DeletionResult;
};

// ── Change a record's type in place (#11700) ─────────────────────────

const changeKindUrl = (entityKind: EntityKind, entityId: string) =>
  `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}/change-kind`;

// What the change would do, from the same planner that performs it.
export const previewEntityKindChange = async (
  entityKind: EntityKind,
  entityId: string,
  targetKind: EntityKind
): Promise<KindChangePlan> => {
  const url = new URL(`${changeKindUrl(entityKind, entityId)}/preview`);
  url.searchParams.set("target_kind", targetKind);
  const response = await fpFetch(url.toString(), { method: "GET" });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to preview type change: ${response.status}`
    );
  }
  return (await response.json()) as KindChangePlan;
};

export const changeEntityKind = async (
  queryClient: QueryClient,
  entityKind: EntityKind,
  entityId: string,
  targetKind: EntityKind
): Promise<KindChangeResult> => {
  const response = await fpFetch(changeKindUrl(entityKind, entityId), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_kind: targetKind }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to change type: ${response.status}`
    );
  }
  const result = (await response.json()) as KindChangeResult;

  // The record moved between kind-scoped lists and its facts changed shape:
  // drop the search caches and refetch everything else, as a delete does.
  queryClient.removeQueries({
    predicate: (query) =>
      query.queryKey[0] === "rms" && query.queryKey[1] === "search",
  });
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "rms" && query.queryKey[1] !== "search",
  });
  return result;
};

// Per-org document column labels used to be applied here, off a map keyed by
// one client's prod and stage organization IDs. That published those IDs and
// the client's vocabulary in a bundle anyone can fetch, so the rename moved to
// the backend (#10972) — the schema now arrives with `display_name` resolved.

// Options for an enum fact whose schema entry defers its option list via
// `values_endpoint` (#10758) rather than shipping them in `scalar_values`.
// The endpoint returns `{ values: string[] }`; the filter clause fetches it
// when the fact's filter opens. `endpoint` is the API path (host-relative)
// the schema fact supplied.
export const fetchFactValues = async (endpoint: string): Promise<string[]> => {
  const response = await fpFetch(`${BASE_API_URL_V2}${endpoint}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to fetch filter options: ${response.status}`
    );
  }

  const data = await response.json();
  return (data.values ?? []) as string[];
};

export const fetchEntitySchema = async (
  entityKind: EntityKind | string,
  organizationId?: string,
  flat?: boolean,
  // A dataset's record tab (#12178): the record the grid is scoped to, so the
  // schema can name the column that would repeat it on every row.
  scopePublicId?: string
): Promise<RmsSchema> => {
  const url = new URL(`${BASE_API_URL_V2}/api/v2/rms/schema/${entityKind}`);
  if (scopePublicId) {
    url.searchParams.set("scope_entity_public_id", scopePublicId);
  }
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }
  if (flat) {
    url.searchParams.set("flat", "true");
  }

  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to fetch entity schema: ${response.status}`
    );
  }

  const data: RmsSchema = await response.json();
  return data;
};

// Per-attribute population of a record type (#11426 gaps-v9): how many of
// the kind's records carry a current value, and the kind's record total.
export interface SchemaFill {
  entity_total: number;
  filled: Record<string, number>;
}

export const fetchSchemaFill = async (
  entityKind: EntityKind | string,
  organizationId?: string
): Promise<SchemaFill> => {
  const url = new URL(
    `${BASE_API_URL_V2}/api/v2/rms/schema/${entityKind}/fill`
  );
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }

  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to fetch schema fill: ${response.status}`
    );
  }

  const data: SchemaFill = await response.json();
  return data;
};

// The values an attribute's records hold, most used first (#12229): the
// options a free-text field starts with when it becomes a dropdown, and the
// record counts behind the editor's "Merging X into Y" line.
export interface OptionUsage {
  values: { value: any; records: number }[];
  synced_from_dealcloud: boolean;
  // DealCloud label -> the option it is stored as, for each renamed label
  // (#12485). An option absent from it stands for its own name.
  dealcloud_labels: Record<string, string>;
}

export const fetchOptionUsage = async (
  entityKind: EntityKind | string,
  attributeId: string,
  organizationId?: string
): Promise<OptionUsage> => {
  const url = new URL(
    `${BASE_API_URL_V2}/api/v2/rms/schema/${entityKind}/attributes/${attributeId}/option-usage`
  );
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }
  const response = await fpFetch(url.toString(), { method: "GET" });
  if (!response.ok) {
    throw new Error(`Failed to fetch option usage: ${response.status}`);
  }
  const data: OptionUsage = await response.json();
  return data;
};

// How a connector syncs one attribute (#12228). The backend owns every word
// here; the field editor only lays it out.
export interface AttributeSync {
  connector: string;
  direction: "inbound" | "outbound" | "both";
  remote_field: string | null;
  cadence: string[];
  local_edit: string;
  // The connector's option label, and the label shown in its place.
  option_map: { source_label: string; shown_as: string }[];
}

// Attribute id -> one entry per connector that syncs it. An attribute no live
// connector syncs is absent.
export interface SchemaSync {
  attributes: Record<string, AttributeSync[]>;
}

export const fetchSchemaSync = async (
  entityKind: EntityKind | string,
  organizationId?: string
): Promise<SchemaSync> => {
  const url = new URL(
    `${BASE_API_URL_V2}/api/v2/rms/schema/${entityKind}/sync`
  );
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }

  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to fetch schema sync: ${response.status}`
    );
  }

  const data: SchemaSync = await response.json();
  return data;
};

export const applySchemaChanges = async (
  queryClient: QueryClient,
  entityKind: EntityKind,
  changes: SchemaChanges,
  organizationId?: string,
  forceRemoveEnumValues?: boolean
): Promise<RmsSchema> => {
  const url = new URL(
    `${BASE_API_URL_V2}/api/v2/rms/schema/${entityKind}/changes`
  );
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }
  if (forceRemoveEnumValues) {
    url.searchParams.set("force_remove_enum_values", "true");
  }

  const response = await fpFetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(changes),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Someone else changed what this save was made against. Not a failure:
    // the editor offers to reload.
    if (response.status === 409 && errorData?.conflict) {
      throw { message: errorData.message, conflict: true };
    }

    // If it's a structured error with entities (ErrorEntitiesViolateRule)
    if (errorData && errorData.entities) {
      throw {
        message: errorData.message || "Entities violate schema rules",
        entities: errorData.entities,
      };
    }

    // If it's a structured error with just a message (ErrorSchemaInvalid)
    if (errorData && errorData.message) {
      throw {
        message: errorData.message,
      };
    }

    // Fallback error
    throw {
      message:
        errorData.detail || `Failed to update schema: ${response.status}`,
    };
  }

  const updatedSchema = await response.json();

  // Update the React Query cache with the fresh schema data
  queryClient.setQueryData(
    SCHEMA_QUERY_KEYS.entitySchema(entityKind, organizationId),
    updatedSchema
  );
  // The Settings editor reads its schema from the bootstrap. Its next save
  // names the versions it holds, so it must hold the ones this save made:
  // waiting for the refetch below would let a quick second edit name the old
  // version and be refused as a conflict with the user's own change. The
  // bootstrap is the signed-in user's organization: a save to another one
  // (the admin template editor) does not belong in it.
  if (!organizationId) {
    queryClient.setQueriesData(
      { queryKey: RMS_BOOTSTRAP_QUERY_KEY },
      (bootstrap: { schemas?: Record<string, RmsSchema> } | undefined) =>
        bootstrap?.schemas?.[entityKind]
          ? {
              ...bootstrap,
              schemas: { ...bootstrap.schemas, [entityKind]: updatedSchema },
            }
          : bootstrap
    );
  }

  // Invalidate entire RMS cache to avoid any stale relationship bugs
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "rms",
  });

  return updatedSchema;
};

// Set or clear an attribute's designation ("this attribute is the fund's
// manager" / "the person's role"). Superadmin-only; setting MOVES the
// designation server-side, clearing any previous holder in the same
// transaction — the returned `cleared_attribute_ids` name the moved-from
// attributes so the caller can update its local schema state. #11270
export const setAttributeDesignation = async (
  queryClient: QueryClient,
  entityKind: EntityKind,
  attributeId: string,
  designates: AttributeDesignation | null,
  organizationId?: string
): Promise<{
  attribute_id: number;
  designates: AttributeDesignation | null;
  cleared_attribute_ids: number[];
}> => {
  const url = new URL(
    `${BASE_API_URL_V2}/api/v2/rms/attributes/${attributeId}/designation`
  );
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }

  const response = await fpFetch(url.toString(), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ designates }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to update the designation: ${response.status}`
    );
  }

  const result = await response.json();

  // Re-read the schema AFTER this commit. A designation is normally applied
  // right behind a schema save, and that save's cache invalidation has
  // already put a schema GET in flight — one whose request predates this
  // write, so its response carries the OLD designation. Landing last, it
  // overwrites the caller's optimistic update and the badge silently
  // disappears while the database is correct. Refetching here means the last
  // response always post-dates the write, so the view converges on truth
  // instead of racing it.
  // type "all", not the default "active": the cached schema should be
  // corrected whether or not something is subscribed right now, so a later
  // mount reads truth rather than the stale entry. This query serves the
  // admin entity editor, which names its organization; for a caller with no
  // organization (Settings, Setup) it matches only an unused entry, and the
  // bootstrap re-read below is the one that counts.
  await queryClient.refetchQueries({
    queryKey: SCHEMA_QUERY_KEYS.entitySchema(entityKind, organizationId),
    type: "all",
  });
  // The schema editor in Settings reads its schema from the RMS bootstrap,
  // not the query above, and the schema save that runs just before this put
  // a bootstrap read in flight whose answer predates the write (#13069: a
  // second email field's badge vanished until a reload). This invalidation
  // replaces that read: its refetch cancels an in-flight fetch of a query
  // that has data before it starts the new one. It refetches only the active
  // entry: the bootstrap is fetched for the acting organization whatever its
  // key says. An entry nobody reads right now is only marked stale, so its
  // next reader re-reads it rather than keep the old designation (the
  // bootstrap is read once per session, spec rms-bootstrap-once-per-session).
  // The refetched entry ends fresh, so the next page move reads nothing.
  await queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });

  return result;
};

// Whether the field's definition changed. Its group and position are not part
// of it: a move is its own operation.
const definitionChanged = (fact: SchemaFact, existing: SchemaFact): boolean => {
  return (
    fact.name !== existing.name ||
    fact.description !== existing.description ||
    fact.is_required !== existing.is_required ||
    fact.ai_autofill !== existing.ai_autofill ||
    fact.scalar_type !== existing.scalar_type ||
    fact.scalar_unit !== existing.scalar_unit ||
    JSON.stringify(fact.scalar_values) !==
      JSON.stringify(existing.scalar_values) ||
    JSON.stringify(fact.enum_colors) !== JSON.stringify(existing.enum_colors) ||
    JSON.stringify(fact.entity_types) !==
      JSON.stringify(existing.entity_types) ||
    fact.inverse_name !== existing.inverse_name ||
    fact.renew_after !== existing.renew_after ||
    fact.ai_cadence !== existing.ai_cadence ||
    fact.ai_cadence_day !== existing.ai_cadence_day ||
    fact.is_array !== existing.is_array ||
    fact.is_enum !== existing.is_enum ||
    fact.is_hierarchical !== existing.is_hierarchical ||
    (fact.show_backreferences ?? true) !==
      (existing.show_backreferences ?? true) ||
    fact.is_required !== existing.is_required ||
    fact.is_range !== existing.is_range ||
    JSON.stringify(fact.default_scalar_value) !==
      JSON.stringify(existing.default_scalar_value) ||
    JSON.stringify(fact.default_entity_ids) !==
      JSON.stringify(existing.default_entity_ids)
  );
};

const convertToAttributeDetail = (
  fact: SchemaFact,
  existing: SchemaFact | null
): AttributeDetail => {
  return {
    name: fact.name,
    inverse_name: fact.inverse_name,
    scalar_type: fact.scalar_type,
    scalar_unit: fact.scalar_unit,
    scalar_values: fact.scalar_values,
    enum_colors: fact.enum_colors,
    enum_renames: fact.enum_renames,
    // The options the save was edited from (#12227): the editor's own base
    // when it tracked one, else the cached field the edit was made against.
    // A new attribute has nothing stored to be stale about.
    enum_base_values:
      existing && fact.scalar_values
        ? (fact.enum_base_values ?? existing.scalar_values ?? null)
        : null,
    entity_types: fact.entity_types,
    is_required: fact.is_required,
    is_array: fact.is_array,
    is_hierarchical: fact.is_hierarchical,
    show_backreferences: fact.show_backreferences ?? true,
    ai_autofill: fact.ai_autofill,
    renew_after: fact.renew_after,
    ai_cadence: fact.ai_cadence,
    ai_cadence_day: fact.ai_cadence_day,
    description: fact.description,
    default_scalar_value: fact.default_scalar_value,
    default_entity_ids: fact.default_entity_ids,
    group_id: fact.group_id,
  };
};

// The operations a save is made of (`SchemaOp`). A schema save takes these
// and nothing else: what the user did, said by the code that did it. There is
// no field list to compare against the cache, so a change no code named is
// never sent, and neither is a field the user left alone (a colleague's
// change, or a change whose own save is still in flight).

// A new field. Built-in and unsaved fields have no version to name.
export const createField = (field: SchemaFact): SchemaOp => ({
  op: "create",
  field,
});

// An edit of `stored`, the field as the edit started from. `field.version` is
// the version the edit was made on (a form keeps it across refetches), which
// the server checks.
export const editField = (field: SchemaFact, stored: SchemaFact): SchemaOp => {
  // Only org fields carry a version, and only they can be edited. An edit
  // with nothing to name would be dropped, so it fails loudly instead.
  if (!field.version && !stored.version) {
    throw new Error(`Field ${field.id} has no version and cannot be edited`);
  }
  return { op: "edit", field, stored };
};

// A delete of the field as the user saw it, with its version.
export const deleteField = (field: SchemaFact): SchemaOp => {
  if (!field.version) {
    throw new Error(`Field ${field.id} has no version and cannot be deleted`);
  }
  return { op: "delete", id: field.id, version: field.version };
};

// A move before `before_id` in group `group_id`, or to the group's end.
export const moveField = (
  id: string,
  to: { group_id: number | null; before_id: string | null }
): SchemaOp => ({
  op: "move",
  id,
  group_id: to.group_id,
  before_id: to.before_id,
});

export const createGroup = (name: string): SchemaOp => ({
  op: "create_group",
  name,
});

export const renameGroup = (
  id: number,
  fromName: string,
  toName: string
): SchemaOp => ({
  op: "rename_group",
  id,
  from_name: fromName,
  to_name: toName,
});

// `fieldIds`: the fields the user saw in the group, which the delete ungroups.
export const deleteGroup = (id: number, fieldIds: string[]): SchemaOp => ({
  op: "delete_group",
  id,
  field_ids: fieldIds,
});

// The request a list of operations makes. An edit that changes nothing in
// the definition (only the group, which is its own move) sends no update,
// so it cannot be refused against a colleague's change it does not touch.
export const buildSchemaChanges = (ops: SchemaOp[]): SchemaChanges => {
  const changes: SchemaChanges = {
    creates: [],
    updates: [],
    deletes: [],
    moves: [],
    group_creates: [],
    group_renames: [],
    group_deletes: [],
  };
  for (const op of ops) {
    switch (op.op) {
      case "create":
        changes.creates.push(convertToAttributeDetail(op.field, null));
        break;
      case "edit":
        if (definitionChanged(op.field, op.stored)) {
          changes.updates.push({
            id: op.field.id,
            // editField refuses a field with neither.
            version: (op.field.version ?? op.stored.version) as string,
            payload: convertToAttributeDetail(op.field, op.stored),
          });
        }
        break;
      case "delete":
        changes.deletes.push({ id: op.id, version: op.version });
        break;
      case "move":
        changes.moves.push({
          id: op.id,
          group_id: op.group_id,
          before_id: op.before_id,
        });
        break;
      case "create_group":
        changes.group_creates.push({ name: op.name });
        break;
      case "rename_group":
        if (op.from_name !== op.to_name) {
          changes.group_renames.push({
            id: op.id,
            from_name: op.from_name,
            to_name: op.to_name,
          });
        }
        break;
      case "delete_group":
        changes.group_deletes.push({ id: op.id, field_ids: op.field_ids });
        break;
      default: {
        const unknown: never = op;
        throw new Error(`Unknown schema operation ${JSON.stringify(unknown)}`);
      }
    }
  }
  return changes;
};

export const handleSaveSchema = async (
  queryClient: QueryClient,
  entityKind: EntityKind,
  ops: SchemaOp[],
  organizationId?: string,
  forceRemoveEnumValues?: boolean
): Promise<RmsSchema> =>
  applySchemaChanges(
    queryClient,
    entityKind,
    buildSchemaChanges(ops),
    organizationId,
    forceRemoveEnumValues
  );

export const createEntity = async (
  queryClient: QueryClient,
  entityKind: EntityKind,
  name: string,
  facts: Record<string, EntityCreationFact>,
  factDefinitions: SchemaFact[]
): Promise<EntitySchema> => {
  // Facts are already in the correct EntityCreationFact format
  const payload: EntityCreationPayload = {
    name,
    facts,
  };

  // The new record lands in the record cache, so its derived instants are
  // built in the viewer's zone, as a fresh read's are (#14680).
  const zone = new URLSearchParams({ user_tz: currentViewerTimeZone() });
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}?${zone.toString()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // If it's a structured error (ErrorEntitiesViolateRule or ErrorSchemaInvalid)
    if (errorData && typeof errorData === "object") {
      // Check for entities array (ErrorEntitiesViolateRule)
      if (errorData.entities && Array.isArray(errorData.entities)) {
        throw {
          message:
            errorData.message ||
            errorData.detail ||
            "Entities violate schema rules",
          entities: errorData.entities,
        };
      }

      // Check for message or detail field (ErrorSchemaInvalid)
      if (errorData.message || errorData.detail) {
        throw {
          message: errorData.message || errorData.detail,
        };
      }
    }

    // Fallback error handling
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to create entity: ${response.status}`
    );
  }

  const createdEntity = await response.json();

  // Drop search query caches so the next observer attach refetches and
  // surfaces the newly-created entity. Visible-table clearing is the
  // caller's responsibility via `useRefreshRmsSearch`. (#9598)
  queryClient.removeQueries({
    predicate: (query) =>
      query.queryKey[0] === "rms" && query.queryKey[1] === "search",
  });

  // Invalidate other RMS caches (entity details, schema, etc.) to trigger refetch
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "rms" && query.queryKey[1] !== "search",
  });

  return createdEntity as EntitySchema;
};

// ============= Interaction API Functions =============

export const fetchInteraction = async (
  interactionId: string,
  signal?: AbortSignal
): Promise<InteractionResponse> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}`,
    {
      method: "GET",
      signal,
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch interaction: ${response.status}`);
  }

  const data = await response.json();
  return data as InteractionResponse;
};

export interface TranscriptUtterance {
  utterance_index: number;
  source: string | null;
  speaker_name: string | null;
  text: string;
  start_ms: number | null;
  end_ms: number | null;
  /** ISO wall-clock time the utterance was captured (#10002). */
  created_at: string | null;
}

/** How a recording captured its speakers (#11884): on a `call` the mic is
 * the reader's side; in a `room` one microphone heard everyone and no line
 * is the reader's. Null before finalize and for older recordings. */
export type CaptureShape = "call" | "room";

export interface TranscriptUtterancesResult {
  utterances: TranscriptUtterance[];
  captureShape: CaptureShape | null;
}

/**
 * Per-utterance timing for an interaction's transcript (#10002), used to show
 * each line's time into the meeting, plus how the recording captured its
 * speakers (#11884). Empty for legacy/batch transcripts that have no
 * utterance rows.
 */
export const fetchTranscriptUtterances = async (
  interactionId: string
): Promise<TranscriptUtterancesResult> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/transcript-utterances`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch transcript utterances: ${response.status}`
    );
  }

  const data = await response.json();
  const shape = data?.capture_shape;
  return {
    utterances: (data?.utterances ?? []) as TranscriptUtterance[],
    captureShape: shape === "call" || shape === "room" ? shape : null,
  };
};

export const fetchInteractionByBlobId = async (
  blobId: string,
  signal?: AbortSignal
): Promise<InteractionByBlobResponse | null> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/by-blob/${blobId}`,
    {
      method: "GET",
      signal,
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch interaction by blob: ${response.status}`);
  }

  const data = await response.json();
  return data as InteractionByBlobResponse | null;
};

export const createInteraction = async (
  payload: InteractionCreatePayload
): Promise<InteractionResponse> => {
  const response = await fpFetch(`${BASE_API_URL_V2}/api/v2/rms/interactions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // The API states a rejection's reason in `detail` (django-ninja's
    // HttpError shape); `message` is the older convention. Reading only the
    // latter reduced a 400 that names the bad field to a bare status code
    // (#11331).
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to create interaction: ${response.status}`
    );
  }

  const data = await response.json();
  return data as InteractionResponse;
};

/**
 * A notes save refused because the stored notes changed after the text it was
 * typed on (#12212). `notes` is what is stored now, for the editor to re-base
 * its unsaved typing on.
 */
export class NotesConflictError extends Error {
  constructor(readonly notes: string | null) {
    super("The notes changed after this edit started");
    this.name = "NotesConflictError";
  }
}

export const updateInteraction = async (
  interactionId: string,
  payload: InteractionUpdatePayload
): Promise<InteractionResponse> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 409 && errorData.error === "notes_conflict") {
      throw new NotesConflictError(
        typeof errorData.notes === "string" ? errorData.notes : null
      );
    }
    throw new Error(
      errorData.message || `Failed to update interaction: ${response.status}`
    );
  }

  const data = await response.json();
  return data as InteractionResponse;
};

export const deleteInteraction = async (
  interactionId: string
): Promise<{ message: string }> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to delete interaction: ${response.status}`
    );
  }

  const data = await response.json();
  return data as { message: string };
};

export interface InteractionTranscriptResponse {
  transcript: string | null;
}

/** Someone a transcript speaker can be renamed to (#11884). */
export interface SpeakerCandidate {
  name: string;
  user_id: string | null;
  email: string | null;
  /** A colleague who turned voice recognition on. */
  enrolled: boolean;
}

export interface TranscriptSpeaker {
  label: string;
  lines: number;
  /** An unnamed speaker's voice sounds like this person, not clearly enough
   * to have named them; for an unconfirmed name, who recognition named. */
  suggestion?: SpeakerCandidate | null;
  /** A name recognition gave only because the person was on the invite. */
  unconfirmed?: boolean;
}

export interface TranscriptSpeakers {
  speakers: TranscriptSpeaker[];
  people: SpeakerCandidate[];
  /** Whether the organization has Speaker Identification on; off, the
   * transcript offers no list, suggestion or rename (#11884). */
  speaker_identification: boolean;
  /** What "New speaker" would mint now for a line of each label, by the
   * server's own rule (#13917); a label whose lines sit on both sides of the
   * conversation has none. */
  new_speaker_for?: Record<string, string>;
}

/** The transcript's speaker labels and the people they could be (#11884). */
export const fetchTranscriptSpeakers = async (
  interactionId: string
): Promise<TranscriptSpeakers> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/speakers`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch transcript speakers: ${response.status}`);
  }
  return (await response.json()) as TranscriptSpeakers;
};

export interface SpeakerRenameResult {
  renamed: number;
  /** The voice was added to the tagged colleague's voice profile. */
  voice_saved: boolean;
  transcript: string | null;
}

/**
 * Rename every line of one speaker (#11884). `userId` names a colleague, whose
 * voice is kept for later recordings when they turned recognition on;
 * `entityId` a person record, which the server tags as a participant of the
 * meeting (#14412).
 */
export const renameTranscriptSpeaker = async (
  interactionId: string,
  label: string,
  name: string,
  userId?: string,
  entityId?: string
): Promise<SpeakerRenameResult> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/speakers/rename`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        label,
        name,
        user_id: userId ?? null,
        ...(entityId ? { entity_id: entityId } : {}),
      }),
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to rename speaker: ${response.status}`
    );
  }
  return (await response.json()) as SpeakerRenameResult;
};

export interface SpeakersConfirmedResult {
  /** Every name confirmed, and whether its voice was kept for its person. */
  confirmed: { label: string; voice_saved: boolean }[];
  transcript: string | null;
}

/**
 * Mark a transcript's speaker names as correct (#14225): every name not yet
 * confirmed is confirmed at once.
 */
export const confirmTranscriptSpeakers = async (
  interactionId: string
): Promise<SpeakersConfirmedResult> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/speakers/confirm-all`,
    { method: "POST" }
  );
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  return (await response.json()) as SpeakersConfirmedResult;
};

/** One line of the stored transcript text as the reader saw it (#13917):
 * its index in the text split on newlines, its speaker label, and its text
 * after `label: `. The server refuses the move when the line changed. */
export interface TranscriptLineRef {
  index: number;
  label: string;
  text: string;
}

/** A move was refused because the transcript changed under the reader
 * (#13917): the view should read the transcript again. */
export class TranscriptChangedError extends Error {}

export interface SpeakerReassignResult {
  reassigned: number;
  /** The label the lines now carry, given or minted. */
  speaker: string;
  transcript: string | null;
}

/**
 * Move transcript lines to another of the transcript's voices (#13917):
 * `speaker` is a label the text carries, or null for a new placeholder
 * voice the server names.
 */
export const reassignTranscriptSpeaker = async (
  interactionId: string,
  lines: TranscriptLineRef[],
  speaker: string | null
): Promise<SpeakerReassignResult> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/speakers/reassign`,
    {
      method: "POST",
      // A stale view (409) is an answer the transcript acts on: it reloads.
      expectedStatuses: [409],
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lines, speaker }),
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      errorData.detail ||
      errorData.message ||
      `Failed to move the line: ${response.status}`;
    throw response.status === 409
      ? new TranscriptChangedError(message)
      : new Error(message);
  }
  return (await response.json()) as SpeakerReassignResult;
};

// Edit/trim a meeting transcript. Sending an empty string removes it entirely.
export const updateInteractionTranscript = async (
  interactionId: string,
  transcript: string
): Promise<InteractionTranscriptResponse> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/transcript`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ transcript }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to update transcript: ${response.status}`
    );
  }

  const data = await response.json();
  return data as InteractionTranscriptResponse;
};

// Remove a meeting transcript entirely, leaving notes and the AI summary intact.
export const deleteInteractionTranscript = async (
  interactionId: string
): Promise<InteractionTranscriptResponse> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/transcript`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to delete transcript: ${response.status}`
    );
  }

  const data = await response.json();
  return data as InteractionTranscriptResponse;
};

// ============= Activity Log API Functions =============
//
// Compiler-driven read of rms_audit_log via /api/v2/rms/activity-log/search.
// Body shape matches /rms/entities/search — filters / sorts / pagination
// flow through the standard RMS search machinery.

import type { PolymorphicCellValue, UnifiedSearchResponse } from "./types";
import type { Temporal } from "@/utils/temporal";

export interface ActivityLogRow {
  keyset_id: string;
  entity_id: number;
  // The moment this row records, in the temporal wire contract (#11055). An
  // audit timestamp is an instant, so which calendar day it lands on is the
  // reader's question rather than the payload's, and the feed answers it in
  // the zone the viewer resolved. Null when the Time cell states no moment,
  // which the feed drops rather than filing under a day it invented.
  occurred_at: Temporal | null;
  action: "create" | "update" | "delete";
  // Finer verb derived server-side from action + target FKs +
  // details_after keys. Stable enum-style id — the frontend formats
  // into display English via ACTIVITY_VERB_LABEL.
  verb: string;
  user_id: string | null;
  // users.name resolved server-side (null for AI rows and for tokens
  // that don't match a users record — API keys, scripts).
  actor_display: string | null;
  // Derived server-side: row was written by the AI pipeline OR by a
  // @finpilot.ai team member acting manually. Frontend renders both
  // cases as "Finpilot AI ✨" — the actual email is never sent down.
  is_finpilot_ai: boolean;
  target: PolymorphicCellValue | null;
  source: PolymorphicCellValue | null;
  llm_reasoning: string | null;
  // Comma-joined fact attribute names for update-fact rows; null/empty
  // for non-fact actions. Powers the "Fact" column on the Activity grid.
  fact_name: string | null;
  reviewed: boolean | null;
  review_id: number | null;
  // rms_review.reviewed_by_user → users.name (resolved post-fetch).
  approved_by_display: string | null;
  approved_at: string | null;
  // Legacy update-schema rows stored `before` as a list; shape is
  // inspected at render time.
  details_before: unknown;
  details_after: unknown;
  // DENSE_RANK over group keys when the request includes `group_by`;
  // null otherwise. Callers cluster sub-rows client-side by this rank.
  group_rank: number | null;
  // Stamped only on the surviving row of a feed-collapse pass when this
  // row was part of a bulk RMS table-view operation (drag-fill, autofill,
  // clear-row) that touched multiple targets. Renders as
  // "and N − 1 other entities" alongside the kept target name. Null
  // outside the feed (grid view) and null for singleton bulks.
  bulk_target_count?: number | null;
  // A same-actor same-verb run the enhanced feed folded (#11876), set only
  // on the run's leader. The counts cover the whole run; `run_members` are
  // the first rows after the leader (enough for the panel's visible cap);
  // `run_head_keyset_id` is the leader's own keyset, from which the rest of
  // the run pages, and `keyset_id` on the leader is the run's tail.
  run_event_count: number | null;
  run_object_count: number | null;
  // The one kind every object in the run has, or null for a mixed run.
  run_object_kind: string | null;
  run_head_keyset_id: string | null;
  run_members: ActivityLogRow[] | null;
  // The records a document-side link edit added and removed, resolved
  // server-side from the row's `linked_entities` before/after (#13433).
  // Null on every other row.
  linked?: PolymorphicCellValue[] | null;
  unlinked?: PolymorphicCellValue[] | null;
}

/** One page of the prose feed: its rows and whether the log continues below
 * them. The backend says so outright for the enhanced feed (#11876), because
 * a page folds runs and can be short while older rows remain; a response
 * without the flag falls back to the full-page rule. */
export interface ActivityFeedPage {
  rows: ActivityLogRow[];
  hasMore: boolean;
  // Where the next page starts when this one has no rows to resume below
  // (every row the backend read was hidden); null otherwise.
  nextKeysetId: string | null;
}

/** Names a folded run for `fetchActivityRunMembers`: the leader's own
 * keyset and the run's tail, plus the feed's own filter so the members are
 * read under the same scope. */
export interface ActivityRunMembersBody {
  head_keyset_id: string;
  tail_keyset_id: string;
  filter?: string;
}

/** Body shape matches SearchBody on the backend (rms_search.SearchBody). */
export interface ActivityLogSearchBody {
  filter?: string; // JSON array of {name, op, value}
  sort_by?: string[];
  sort_order?: ("asc" | "desc")[];
  group_by?: string[];
  keyset_id?: string;
  page_size?: number;
  // Opt-in backend post-processing: dedup consecutive same-field edits
  // within a short window + splice entity_display onto details_after
  // entity-ref fact values. Used by the sidebar; the tabular view
  // leaves this off (display enhancements don't fit the grid cell UX).
  enhance_display?: boolean;
}

/** Permanently delete an audit row. Superadmin-gated server-side; the
 * UI hides the entry point for non-admins. */
export const deleteActivityLogRow = async (auditId: number): Promise<void> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/activity-log/row/${auditId}`,
    { method: "DELETE" }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to delete activity row: ${response.status}`
    );
  }
};

/**
 * Detach documents from one record (#13426): remove each document's link to
 * the record, leaving the document in the organization. One call per
 * document, through the endpoint the Documents list's Detach uses. Every
 * document is attempted. A 404 means the record has no direct link to that
 * document (it reaches it through a vehicle or an interaction), which is
 * reported apart from any other failure.
 */
export const detachDocumentsFromEntity = async (
  entityId: string,
  blobIds: string[]
): Promise<DetachOutcome> => {
  const outcome: DetachOutcome = { detached: [], notLinked: [], failed: [] };
  await Promise.all(
    blobIds.map(async (blobId) => {
      try {
        const response = await fpFetch(
          `${BASE_API_URL_V2}/api/v2/rms/document-relationship`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ blob_id: blobId, entity_id: entityId }),
          }
        );
        if (response.ok) outcome.detached.push(blobId);
        else if (response.status === 404) outcome.notLinked.push(blobId);
        else outcome.failed.push(blobId);
      } catch (err) {
        console.error("Failed to detach document:", err);
        outcome.failed.push(blobId);
      }
    })
  );
  return outcome;
};

export const searchActivityLog = async (
  body: ActivityLogSearchBody
): Promise<ActivityLogRow[]> => (await searchActivityFeedPage(body)).rows;

export const searchActivityFeedPage = async (
  body: ActivityLogSearchBody
): Promise<ActivityFeedPage> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/activity-log/search`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...body,
        user_tz: currentViewerTimeZone(),
      }),
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to search activity log: ${response.status}`
    );
  }
  // The endpoint returns the unified {columns, rows} shape; the prose-
  // style ActivityFeed (sidebar + entity-page Activity tab) consumes the
  // legacy flat ActivityLogRow shape. Translate at the boundary so the
  // ~15 call sites in ActivityFeed.tsx stay untouched. See
  // Data/activityFeedAdapter.ts.
  const payload = (await response.json()) as UnifiedSearchResponse & {
    has_more?: boolean | null;
    next_keyset_id?: string | null;
  };
  const { unifiedToActivityLogRows } = await import(
    "../Data/activityFeedAdapter"
  );
  const rows = unifiedToActivityLogRows(payload);
  return {
    rows,
    hasMore:
      typeof payload.has_more === "boolean"
        ? payload.has_more
        : rows.length >= (body.page_size ?? 100),
    nextKeysetId: payload.next_keyset_id ?? null,
  };
};

/** The rest of a folded run — every row after the leader, in feed order —
 * for "Show N more" on a run whose shipped members did not cover it (#11876). */
export const fetchActivityRunMembers = async (
  body: ActivityRunMembersBody
): Promise<ActivityLogRow[]> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/activity-log/run-members`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...body,
        user_tz: currentViewerTimeZone(),
      }),
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.detail ||
        `Failed to load the rest of the run: ${response.status}`
    );
  }
  const payload = await response.json();
  const { unifiedToActivityLogRows } = await import(
    "../Data/activityFeedAdapter"
  );
  return unifiedToActivityLogRows(payload);
};

// ============= Interaction Types API Functions =============

export interface InteractionType {
  // Stable across renames; the name is not (#13041).
  id: number;
  interaction_type: string;
  description?: string;
  // The organization's default type, which anything that needs a type and
  // has none to hand files under; one per organization (#13041).
  is_default: boolean;
}

export interface InteractionTypeCreatePayload {
  // An existing type to rename or re-describe; absent for a new type.
  id?: number;
  interaction_type: string;
  description?: string;
  // True makes this type the default; false or absent on every item keeps
  // the current one (#13041).
  is_default?: boolean;
}

export const fetchInteractionTypes = async (
  organizationId?: string
): Promise<InteractionType[]> => {
  const url = new URL(`${BASE_API_URL_V2}/api/v2/rms/interactions/types`);
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }

  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch interaction types: ${response.status}`);
  }

  return response.json();
};

export const replaceInteractionTypes = async (
  payload: InteractionTypeCreatePayload[],
  organizationId?: string
): Promise<InteractionType[]> => {
  const url = new URL(`${BASE_API_URL_V2}/api/v2/rms/interactions/types`);
  if (organizationId) {
    url.searchParams.set("organization_id", organizationId);
  }

  const response = await fpFetch(url.toString(), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ types: payload }),
  });

  if (!response.ok) {
    // The server names what it refused (a type still in use, a name already
    // taken), which the settings tab shows as it is.
    throw await errorFromResponse(response);
  }

  return response.json();
};

// Nickname mappings for name matching: formal name -> nicknames.
export type NicknameGroups = Record<string, string[]>;
// Uncached on purpose: the picker reads it through react-query, which is the
// one place a per-session value is shared (#12137).
export const fetchNicknames = async (): Promise<NicknameGroups> => {
  const response = await fpFetch(`${BASE_API_URL_V2}/api/v2/rms/nicknames`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch nicknames: ${response.status}`);
  }

  const data = await response.json();
  return data as NicknameGroups;
};

// Document cell update payload (matches backend DocumentCellUpdate)
interface DocumentCellUpdatePayload {
  column_id: number;
  value?: any;
  entity_ids?: string[] | null;
}

export const updateDocumentCells = async (
  blobId: string,
  cells: DocumentCellUpdatePayload[]
): Promise<any> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/documents/${blobId}/cells`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cells),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update document cells: ${response.status}`);
  }

  return response.json();
};

// Write edited cells back to one transaction (#10997). `rowId` is the grid
// row's id, which for the transactions view is the transactions id. The same
// call serves the global grid and an entity's Transactions tab -- they are one
// grid with a different scope.
export const updateTransactionCells = async (
  rowId: string,
  cells: Array<{ column_id: number; value: unknown }>
): Promise<any> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/transactions/${rowId}/cells`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cells),
    }
  );

  if (!response.ok) {
    // The server's sentence (a non-number in the Amount column, #14991)
    // is what the tab's save state shows.
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? "Failed to update transaction cells");
  }

  return response.json();
};

// Write edited cells back to one holdings snapshot row (#10835). `rowId` is the
// grid row's id, which for the holdings view is the holdings_rows id.
export const updateHoldingsCells = async (
  rowId: string,
  cells: Array<{ column_id: number; value: unknown }>
): Promise<any> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/holdings/rows/${rowId}/cells`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cells),
    }
  );

  if (!response.ok) {
    // The server's sentence when it gives one (a synced row's refusal).
    const detail = await response.json().catch(() => null);
    throw new Error(
      detail?.detail ?? `Failed to update holdings cells: ${response.status}`
    );
  }

  return response.json();
};

// Write the cells of the columns an organization added to a dataset (#12733).
// `rowId` is the grid row's id (the row version's database id); the server
// resolves the row's source key from it. The source's own columns are not
// writable.
export const updateDatasetCells = async (
  datasetId: number,
  rowId: string,
  cells: Array<{ column_id: number; value: unknown }>
): Promise<{ updated: number }> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/datasets/${datasetId}/rows/${encodeURIComponent(rowId)}/cells`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cells),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update dataset cells: ${response.status}`);
  }

  return response.json();
};

// The types a column added to a dataset may take (#12733).
export type DatasetColumnDataType = "string" | "number" | "boolean" | "date";

export interface DatasetColumn {
  id: number;
  key: string;
  name: string;
  data_type: DatasetColumnDataType;
  description: string | null;
  origin: string;
  position: number;
}

// A refused column change carries the server's reason in `detail`; the editor
// shows it as it is.
async function datasetColumnError(
  response: Response,
  fallback: string
): Promise<Error> {
  try {
    const body: unknown = await response.json();
    if (
      body !== null &&
      typeof body === "object" &&
      "detail" in body &&
      typeof body.detail === "string"
    ) {
      return new Error(body.detail);
    }
  } catch {
    // Not JSON: fall through to the generic reason.
  }
  return new Error(`${fallback}: ${response.status}`);
}

export const createDatasetColumn = async (
  datasetId: number,
  column: {
    name: string;
    data_type: DatasetColumnDataType;
    description?: string;
  }
): Promise<DatasetColumn> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/datasets/${datasetId}/columns`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(column),
    }
  );
  if (!response.ok) {
    throw await datasetColumnError(response, "Failed to add column");
  }
  return response.json();
};

export const updateDatasetColumn = async (
  datasetId: number,
  columnId: number,
  changes: { name?: string; description?: string }
): Promise<void> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/datasets/${datasetId}/columns/${columnId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    }
  );
  if (!response.ok) {
    throw await datasetColumnError(response, "Failed to update column");
  }
};

export const deleteDatasetColumn = async (
  datasetId: number,
  columnId: number
): Promise<void> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/datasets/${datasetId}/columns/${columnId}`,
    { method: "DELETE" }
  );
  if (!response.ok) {
    throw await datasetColumnError(response, "Failed to delete column");
  }
};

export const updateInteractionCells = async (
  interactionId: string,
  cells: DocumentCellUpdatePayload[]
): Promise<any> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/cells`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cells),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update interaction cells: ${response.status}`);
  }

  return response.json();
};

// ============= Document Config =============

export const fetchDocumentConfig = async (
  organizationId?: string
): Promise<DocumentConfig> => {
  const params = organizationId
    ? `?organization_id=${encodeURIComponent(organizationId)}`
    : "";
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/document-config${params}`
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch document config: ${response.status}`);
  }
  return response.json();
};

export const replaceDocumentGroups = async (
  groups: Omit<DocumentGroup, "id">[],
  organizationId?: string
): Promise<DocumentConfig> => {
  const params = organizationId
    ? `?organization_id=${encodeURIComponent(organizationId)}`
    : "";
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/document-groups${params}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ groups }),
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to update document groups: ${response.status}`);
  }
  return response.json();
};

export const replaceDocumentTypes = async (
  types: DocumentType[],
  organizationId?: string
): Promise<DocumentConfig> => {
  const params = organizationId
    ? `?organization_id=${encodeURIComponent(organizationId)}`
    : "";
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/document-types${params}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ types }),
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || `Failed to update document types: ${response.status}`
    );
  }
  return response.json();
};

export interface PlacesHint {
  lat?: number | null;
  lng?: number | null;
  place_id?: string | null;
}

export const validateAddress = async (
  address: string,
  hint?: PlacesHint
): Promise<AddressValue> => {
  const body: Record<string, unknown> = { address };
  if (hint) body.hint = hint;
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/validate-address`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to validate address: ${response.status}`);
  }
  return response.json();
};

export const fetchAddressComponentValues = async (
  attributeId: number,
  component: AddressComponent
): Promise<string[]> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/address-component-values`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attribute_id: attributeId, component }),
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch address values: ${response.status}`);
  }
  return response.json();
};

// ============================================================================
// AI autofill on-demand triggers (issue #4914). All three return the id of
// the rms_autofill workspace the backend created; the source-icon click on
// the filled fact opens this workspace's query-answer in the matrix section-
// answer modal.
// ============================================================================

export interface AutofillTriggerResponse {
  workspace_id: string;
  dispatched_attribute_ids: number[];
}

async function _triggerAutofill(
  url: string,
  body?: unknown
): Promise<AutofillTriggerResponse> {
  const response = await fpFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.detail || `Autofill failed: ${response.status}`);
  }
  return response.json();
}

export const triggerAutofillForAttribute = async (
  publicEntityId: string,
  attributeId: number
): Promise<AutofillTriggerResponse> =>
  _triggerAutofill(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${publicEntityId}/attributes/${attributeId}/autofill`
  );

export const triggerAutofillBulk = async (
  publicEntityId: string,
  attributeIds: number[]
): Promise<AutofillTriggerResponse> =>
  _triggerAutofill(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${publicEntityId}/autofill/bulk`,
    { attribute_ids: attributeIds }
  );

export interface ViewAutofillPair {
  entity_public_id: string;
  attribute_id: number;
}

export interface ViewAutofillResponse {
  workspace_id: string;
  // Pairs that survived backend validation (ai_autofill=true on the
  // entity's kind). Pairs whose attribute isn't AI-enabled are silently
  // dropped — the frontend may be slightly stale.
  dispatched_pairs: ViewAutofillPair[];
}

// Bulk autofill across a multi-entity, multi-attribute selection from
// the views grid. Backend creates one rms_autofill workspace and fans
// out one celery task per (entity, attribute) pair, all broadcasting
// RMS_AUTOFILL_ATTEMPT events on the rms_view_<view_room_id> room.
export const triggerAutofillForView = async (
  viewRoomId: string,
  pairs: ViewAutofillPair[]
): Promise<ViewAutofillResponse> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/views/autofill`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ view_room_id: viewRoomId, pairs }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.detail || `Autofill failed: ${response.status}`);
  }
  return response.json();
};

export interface ViewAutofillInFlightResponse {
  pending_pairs: ViewAutofillPair[];
}

// Which cells still have an autofill running, for the entities the grid has
// rendered. The websocket is fire-and-forget, so a terminal event pushed
// while the socket was down never arrives and the cell shimmers until the
// user reloads; the grid calls this on reconnect to resettle against the
// server, which holds the truth in rms_autofill_attempts.
export const fetchViewAutofillInFlight = async (
  entityPublicIds: string[]
): Promise<ViewAutofillInFlightResponse> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/views/autofill/in-flight`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity_public_ids: entityPublicIds }),
    }
  );
  if (!response.ok) {
    throw new Error(`Autofill in-flight lookup failed: ${response.status}`);
  }
  return response.json();
};
